import type { SchoolByIdsDto } from './schoolBands.dto';

/** Matches server empty / new entity id */
export const PARENT_BAND_EMPTY_ID = '00000000-0000-0000-0000-000000000000';

/** GET /api/app/parent-tuition-band — paged item */
export interface ParentTuitionBandDto {
  id?: string;
  name: string;
  fullName?: string | null;
  minValue: number;
  maxValue: number;
  description?: string | null;
  minSchoolsPerBand: number;
  year: number;
  numberOfSchools: number;
  /** Same value on every item — total schools in the parent-market universe for this run. */
  totalNumberOfSchools?: number;
  /** Same value on every item — schools not assigned to any parent-market band. */
  totalNumberOfSchoolsOutsideBands?: number;
  /** School id → enrollment weight (or similar) used for quantile weighting */
  schoolIds?: Record<string, number>;
  /** When returned by populate-band-membership (or future GET), schools assigned to this band. */
  schools?: SchoolByIdsDto[];
}

/** POST /api/app/parent-tuition-band/populate-band-membership — 200 body */
export interface ParentPopulateBandMembershipResult {
  bands: ParentTuitionBandDto[];
  totalNumberOfSchools?: number;
  totalNumberOfSchoolsOutsideBands?: number;
  /** Schools not assigned to any parent-market band for this run. */
  schoolsOutsideBands: SchoolByIdsDto[];
}

export interface ParentTuitionBandsPagedResult {
  items: ParentTuitionBandDto[];
  totalCount: number;
}

/** Local edit state for parent-market step (mirrors SizeBandEditableRow). */
export interface ParentBandEditableRow {
  key: string;
  /** Persisted entity id when known */
  serverId?: string;
  name: string;
  minValue: number;
  maxValue: number;
  description: string;
  numberOfSchools: number;
  schoolIds?: Record<string, number>;
  /** Mirrors `ParentTuitionBandDto` — included when calling populate-band-membership. */
  minSchoolsPerBand?: number;
  year?: number;
  schools?: SchoolByIdsDto[];
}

/**
 * GET /api/app/parent-tuition-band duplicates `totalNumberOfSchools` and
 * `totalNumberOfSchoolsOutsideBands` on each item. Some deployments return `0` for both on every
 * row while `numberOfSchools` per band is correct — treat that pattern as “totals omitted” and
 * infer market size from band counts (outside = 0). When any row carries a positive universe total,
 * trust it and derive outside if the API did not send a usable outside count.
 */
export function pickParentTuitionBandListAggregates(items: ParentTuitionBandDto[]): {
  totalNumberOfSchools: number | undefined;
  totalNumberOfSchoolsOutsideBands: number | undefined;
} {
  if (!items.length) {
    return { totalNumberOfSchools: undefined, totalNumberOfSchoolsOutsideBands: undefined };
  }

  const sumInBands = items.reduce((s, it) => {
    const n = it.numberOfSchools;
    return s + (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : 0);
  }, 0);

  let totalFromApi: number | undefined;
  for (const it of items) {
    const t = it.totalNumberOfSchools;
    if (typeof t === 'number' && Number.isFinite(t) && t > 0) {
      totalFromApi = t;
      break;
    }
  }

  if (totalFromApi != null) {
    let outside: number | undefined;
    for (const it of items) {
      const o = it.totalNumberOfSchoolsOutsideBands;
      if (typeof o === 'number' && Number.isFinite(o) && o >= 0) {
        outside = o;
        break;
      }
    }
    if (outside === undefined) {
      outside = Math.max(0, totalFromApi - sumInBands);
    }
    return {
      totalNumberOfSchools: totalFromApi,
      totalNumberOfSchoolsOutsideBands: outside,
    };
  }

  /** True when the API omits totals or sends 0 on every row (GET often leaves fields off entirely). */
  const unsetOrZero = (v: number | undefined) =>
    v === undefined || (typeof v === 'number' && Number.isFinite(v) && v === 0);

  const totalsLookUnset =
    items.length > 0 &&
    items.every((it) => unsetOrZero(it.totalNumberOfSchools)) &&
    items.every((it) => unsetOrZero(it.totalNumberOfSchoolsOutsideBands));

  if (totalsLookUnset && sumInBands > 0) {
    return {
      totalNumberOfSchools: sumInBands,
      totalNumberOfSchoolsOutsideBands: 0,
    };
  }

  return { totalNumberOfSchools: undefined, totalNumberOfSchoolsOutsideBands: undefined };
}

export function mapParentTuitionBandsToEditableRows(
  items: ParentTuitionBandDto[]
): ParentBandEditableRow[] {
  return items.map((item, i) => {
    const sid = item.id && item.id !== PARENT_BAND_EMPTY_ID ? item.id : undefined;
    return {
      key: sid ?? `parent-band-${i}-${item.name ?? 'band'}`,
      serverId: sid,
      name: item.name ?? '',
      minValue: item.minValue,
      maxValue: item.maxValue,
      description: item.description ?? '',
      numberOfSchools: item.numberOfSchools ?? 0,
      schoolIds: item.schoolIds,
      minSchoolsPerBand: item.minSchoolsPerBand,
      year: item.year,
      schools: item.schools?.length ? item.schools : undefined,
    };
  });
}

/** POST/PUT body — align with backend DTO names (camelCase JSON). */
export interface ParentTuitionBandSaveInput {
  name: string;
  minValue: number;
  maxValue: number;
  description?: string;
  minSchoolsPerBand: number;
  year: number;
}
